import React, { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, MenuList, styled, SwipeableDrawer, Typography } from '@mui/material';
import StringUtils from '../../utils/stringUtils';
import TwRadioButton from '../TwRadioButtons/TwRadioButtons';
import { LoadDocumentType } from '../../redux/api/load/load.model';
import useNotifications from '../../providers/Notifications/Notifications.provider';

const StyledBox = styled(Box)(() => ({
  backgroundColor: '#fff'
}));

export const Puller = styled(Box)(() => ({
  width: '40px',
  height: '4px',
  backgroundColor: '#E5E5E5',
  borderRadius: '4px',
  position: 'absolute',
  top: '20px',
  left: 'calc(50% - 20px)'
}));

const StyledTitle = styled('p')({
  color: '#9CA3AF',
  fontWeigth: 500,
  fontSize: '14px',
  lineHeight: '24px',
  marginLeft: '11px',
  marginBottom: '0px'
});

// Not all document types are allowed during scanner upload.
// Rate confirmation can't be selected.
const menuOptions = [
  LoadDocumentType.billOfLading,
  LoadDocumentType.lumper,
  LoadDocumentType.proofOfDelivery,
  LoadDocumentType.invoice,
  LoadDocumentType.receipt,
  LoadDocumentType.other
];

export interface TwScannerMenuProps {
  isOpen: boolean;
  onClose: (doc: LoadDocumentType, id: string) => void;
  onOpen?: () => void;
  entityToSelect: 'order' | 'trip';
  entityList: {
    value: string;
    label: string;
    customer: string;
    customerOrderNumber: string;
    readableId: string;
  }[];
  ltlEnabled: boolean;
}

const TwScannerMobileMenu: React.FC<TwScannerMenuProps> = ({
  isOpen,
  entityList,
  entityToSelect,
  ltlEnabled,
  onClose,
  onOpen
}): JSX.Element => {
  const [openType, setOpenType] = useState<boolean>(false);
  const [openEntity, setOpenEntity] = useState<boolean>(false);
  const [currentType, setCurrentType] = useState<LoadDocumentType>(LoadDocumentType.empty);
  const [entityId, setEntityId] = useState<string | null>(null);
  const { addNotification } = useNotifications();

  const menuItems = menuOptions.map((docType, index) => {
    const name = StringUtils.capitalizeFirstLetter(docType.replaceAll('_', ' '));
    return (
      <MenuItem
        key={index}
        onClick={(e): void => {
          e.stopPropagation();
        }}
      >
        <TwRadioButton
          label={name}
          name={name}
          value={docType}
          onInputChange={(): void => {
            setCurrentType(docType);
            handleCloseType(docType);
          }}
          checked={currentType === docType}
        />
      </MenuItem>
    );
  });

  const entityItems = useMemo(() => {
    if (entityList && entityList.length > 0) {
      return entityList.map((entity, index) => {
        return (
          <MenuItem
            key={index}
            onClick={(e): void => {
              e.stopPropagation();
            }}
          >
            <TwRadioButton
              label={''}
              name={entity.label}
              value={entity.value}
              onInputChange={(): void => {
                setEntityId(entity.value);
              }}
              checked={!!(entityId && entityId === entity.value)}
            />
            <Box>
              <Typography className="tw-scanner__mobile-menu-entity-select">
                {entity.customer}
              </Typography>
              {entity.readableId && (
                <Typography className="tw-scanner__mobile-menu-entity-select-secondary">
                  {`Cust #${entity.customerOrderNumber} | TB #${entity.readableId}`}
                </Typography>
              )}
            </Box>
          </MenuItem>
        );
      });
    }
  }, [entityList]);

  const handleCloseType = (type: LoadDocumentType): void => {
    if (ltlEnabled) {
      setOpenType(false);
      setOpenEntity(true);

      setCurrentType(type);
    } else {
      setOpenType(false);

      onClose(type, '');

      setEntityId(null);
    }
  };

  const handleCloseEntity = (): void => {
    if (entityId === null) {
      return addNotification({
        message: `Please select an option.`,
        variant: 'error'
      });
    }

    onClose(currentType, entityId);
    setCurrentType(LoadDocumentType.empty);
    setEntityId(null);
    setOpenEntity(false);
    setOpenType(false);
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentType(LoadDocumentType.empty);
      setOpenType(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (entityId !== null) {
      handleCloseEntity();
    }
  }, [entityId]);

  return (
    <>
      {openType && (
        <SwipeableDrawer
          anchor="bottom"
          open={openType}
          disableSwipeToOpen
          onClose={(): void => handleCloseType(LoadDocumentType.empty)}
          onOpen={onOpen ? onOpen : (): any => null}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px'
            }
          }}
        >
          <StyledBox
            sx={{
              paddingTop: '56px',
              paddingBottom: '22px',
              paddingLeft: '11px',
              paddingRight: '11px',
              height: '100%',
              overflow: 'auto'
            }}
            onClick={(e): void => {
              e.stopPropagation();
            }}
          >
            <Puller />
            <StyledTitle>Select document type </StyledTitle>
            <MenuList>{menuItems}</MenuList>
          </StyledBox>
        </SwipeableDrawer>
      )}

      {openEntity && (
        <SwipeableDrawer
          anchor="bottom"
          open={openEntity}
          disableSwipeToOpen
          onClose={(): void => handleCloseEntity()}
          onOpen={(): any => null}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px'
            }
          }}
        >
          <StyledBox
            sx={{
              paddingTop: '56px',
              paddingBottom: '22px',
              paddingLeft: '11px',
              paddingRight: '11px',
              height: '100%',
              overflow: 'auto'
            }}
            onClick={(e): void => {
              e.stopPropagation();
            }}
          >
            <Puller />
            <StyledTitle>{`Select ${entityToSelect}`}</StyledTitle>
            <MenuList>{entityItems}</MenuList>
          </StyledBox>
        </SwipeableDrawer>
      )}
    </>
  );
};

export default TwScannerMobileMenu;
